import { apiLogger, reduxLogger } from './logger';
import { getStorageRaw, STORAGE_KEYS } from '@/core/utils/local-storage';
/**
 * Debug helper for API errors
 *
 * Run these commands in the browser console to debug API errors:
 *
 * 1. View recent API errors:
 *    window.debugApiErrors()
 *
 * 2. View auth debug info:
 *    window.debugAuthInfo()
 *
 * 3. Clear error logs:
 *    window.clearApiErrors()
 */

interface ApiError {
  timestamp?: string;
  url?: string;
  status?: number;
  error?: string;
  details?: unknown;
}

// View API errors stored in sessionStorage
export function debugApiErrors() {
  const errors: ApiError[] = JSON.parse(sessionStorage.getItem('api_errors') || '[]');
  apiLogger.info(`Found ${errors.length} API errors:`);
  errors.forEach((error: ApiError, index: number) => {
    reduxLogger.info(`\n--- Error ${index + 1} ---`);
    reduxLogger.info('Time:', error.timestamp);
    reduxLogger.info('URL:', error.url);
    reduxLogger.info('Status:', error.status);
    reduxLogger.info('Error:', error.error);
    if (error.details) {
      reduxLogger.info('Details:', error.details);
    }
  });
  return errors;
}

// View auth debug information
export function debugAuthInfo() {
  const lastEvent = JSON.parse(sessionStorage.getItem('auth_debug_last_event') || '{}');
  const authData = {
    lastEvent,
    // Removed legacy stytch_session from localStorage in production code
    currentSession: null,
    authUser: getStorageRaw(STORAGE_KEYS.AUTH_USER),
    isAuthenticated: getStorageRaw(STORAGE_KEYS.IS_AUTHENTICATED),
    // Only expose cookies in development — never in production
    cookies: process.env.NODE_ENV === 'development' ? document.cookie : '[redacted]',
  };
  reduxLogger.info('Auth Debug Info:', authData);
  return authData;
}

// Clear API error logs
export function clearApiErrors() {
  sessionStorage.removeItem('api_errors');
  sessionStorage.removeItem('auth_debug_last_event');
  apiLogger.info('Cleared API error logs');
}

// Attach to window for easy console access — dev-only to prevent exposure in production
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.debugApiErrors = debugApiErrors;
  window.debugAuthInfo = debugAuthInfo;
  window.clearApiErrors = clearApiErrors;
}
